import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalShipping,
  Inventory,
  CheckCircle,
  Schedule,
  LocationOn,
  Payment,
  CalendarToday,
  Receipt,
} from '@mui/icons-material';
import Manubar from './Manubar';
import Footer from './Footer';
import Security from './Security';
import Productstraking from './Productstraking';
import secureLocalStorage from "./storage";
import axios from 'axios';
import './m.css';
import './loader.css';

const TrackOrder = () => {
  const order_id = secureLocalStorage.getItem("orderid");
  const token = secureLocalStorage.getItem("n_token");
  const [showorderlist, setShoworderlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!order_id || !token) {
        setError("Missing order ID or token");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = { "order_id": order_id };
        const response = await axios.post(
          `${process.env.REACT_APP_API}website/track_my_order`,
          data,
          {
            headers: { "token": token }
          }
        );
        setShoworderlist(response.data.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching order data:", err);
        setError("Failed to load order details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [order_id, token]);

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': 'warning',
      'processing': 'info',
      'shipped': 'primary',
      'delivered': 'success',
      'cancelled': 'error'
    };
    return statusColors[status?.toLowerCase()] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {/* Header Section */}
      <div className="modal fade offcanvas-modal" id="exampleModal">
        <div className="modal-dialog offcanvas-dialog">
          <div className="modal-content">
            <Manubar />
          </div>
        </div>
      </div>
      
      <header style={{ width: '100%' }}>
        <div className="header-top-bar d-none d-md-block" style={{ width: '100%' }}>
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-3">
                <div className="social-links social-links-dark">
                  <a className="social-link facebook" href="#">
                    <i className="lab la-facebook"></i>
                  </a>
                  <a className="social-link twitter" href="#">
                    <i className="lab la-twitter"></i>
                  </a>
                  <a className="social-link youtube" href="#">
                    <i className="lab la-youtube"></i>
                  </a>
                  <a className="social-link instagram" href="#">
                    <i className="lab la-instagram"></i>
                  </a>
                </div>
              </div>
              <div className="col-9">
                <ul className="header-topbar-nav nav"></ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <Container maxWidth={false} sx={{ py: 4, px: isMobile ? 2 : 4 }} disableGutters>
          {/* Header Section */}
          <Box sx={{ 
            width: '100%', 
            mb: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            color: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={3}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                p: 2
              }}>
                <LocalShipping sx={{ fontSize: isMobile ? 32 : 48 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                  Track Your Order
                </Typography>
                
                <Chip 
                  label={`Order ID: ${showorderlist[0]?.order_no}`} 
                  variant="filled"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              {showorderlist[0]?.order_date && (
                <Box sx={{ 
                  textAlign: isMobile ? 'left' : 'right',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  p: 2,
                  borderRadius: 2
                }}>
                  <Typography variant="caption" display="block">
                    Order Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDate(showorderlist[0].order_date)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Grid container spacing={3} sx={{ width: '100%' }}>
            {/* Tracking Progress */}
            <Grid item xs={12} lg={5} sx={{ width: '50%' }} >
              <Card 
                elevation={4} 
                sx={{ 
                  borderRadius: 3,
                  height: '100%',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'primary.main',
                      borderRadius: '50%',
                      p: 1,
                      color: 'white'
                    }}>
                      <Inventory sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Order Tracking
                    </Typography>
                  </Stack>
                  <Productstraking />
                </CardContent>
              </Card>
            </Grid>

            {/* Order Details */}
            <Grid item xs={12} lg={7} sx={{ width: '47%' }}>
              <Card 
                elevation={4} 
                sx={{ 
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}>
                  <Stack 
                    direction={isMobile ? "column" : "row"} 
                    justifyContent="space-between" 
                    alignItems={isMobile ? "flex-start" : "center"}
                    spacing={2}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Order Items
                    </Typography>
                    <Chip 
                      label={showorderlist[0]?.order_status || 'Processing'} 
                      color="secondary"
                      variant="filled"
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        px: 2
                      }}
                    />
                  </Stack>
                </Box>

                {loading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: 300,
                    flexDirection: 'column',
                    gap: 3
                  }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" color="text.secondary">
                      Loading order details...
                    </Typography>
                  </Box>
                ) : error ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    px: 3
                  }}>
                    <Schedule sx={{ fontSize: 64, color: 'error.main', mb: 3, opacity: 0.7 }} />
                    <Typography variant="h6" color="error" gutterBottom>
                      {error}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Please check your order ID and try again.
                    </Typography>
                  </Box>
                ) : showorderlist.length > 0 ? (
                  <Box sx={{ p: 3 }}>
                    {showorderlist[0]?.products?.map((item, index) => (
                      <Box key={index}>
                        <Paper 
                          elevation={2} 
                          sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                              borderColor: 'primary.light'
                            }
                          }}
                        >
                          <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={3} sx={{ textAlign: 'center' }}>
                              <Avatar
                                src={`${process.env.REACT_APP_API}uploads/${item.product_image}`}
                                sx={{ 
                                  width: 120, 
                                  height: 120, 
                                  margin: '0 auto',
                                  border: '3px solid',
                                  borderColor: 'primary.light',
                                  borderRadius: 2
                                }}
                                variant="rounded"
                              />
                            </Grid>
                            <Grid item xs={12} sm={9}>
                              <Stack spacing={2}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                  {item.product_name}
                                </Typography>
                                <Stack 
                                  direction={isMobile ? "column" : "row"} 
                                  spacing={isMobile ? 2 : 4}
                                  sx={{ width: '100%' }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Quantity
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                      {item.quantity}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Price
                                    </Typography>
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                        color: 'primary.main'
                                      }}
                                    >
                                      ₹{item.price}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography width={"100px"} variant="caption" color="text.secondary" display="block" gutterBottom>
                                      No Of Products
                                    </Typography>
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                        color: 'primary.main'
                                      }}
                                    >
                                      {item.no_of_product}
                                    </Typography>
                                  </Box>
                                  {item.size && (
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Size
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                        {item.size}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                                {item.tracking_status && (
                                  <Chip
                                    icon={<CheckCircle />}
                                    label={item.tracking_status.toUpperCase()}
                                    color={getStatusColor(item.tracking_status)}
                                    size="medium"
                                    variant="filled"
                                    sx={{ 
                                      fontWeight: 600,
                                      alignSelf: 'flex-start'
                                    }}
                                  />
                                )}
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>
                        {index < showorderlist[0]?.products?.length - 1 && (
                          <Divider sx={{ my: 2, opacity: 0.5 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 10,
                    px: 3
                  }}>
                    <Schedule sx={{ fontSize: 80, color: 'text.secondary', mb: 3, opacity: 0.3 }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      No Order Found
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, margin: '0 auto' }}>
                      We couldn't find any order with the provided ID. Please check your order ID and try again.
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>

          {/* Order Summary Section */}
          {showorderlist.length > 0 && showorderlist[0] && (
            <Grid container spacing={3} sx={{ mt: 3, width: '100%' }}>
              <Grid item xs={12} md={4} sx={{ width: '33%' }}>
                <Card 
                  elevation={3} 
                  sx={{ 
                    borderRadius: 3,
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'primary.main',
                        borderRadius: '50%',
                        p: 1,
                        color: 'white'
                      }}>
                        <LocationOn sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Shipping Address
                      </Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', textAlign: "start" }}>
                      {showorderlist[0]?.address + ', ' + showorderlist[0]?.city + ', ' + showorderlist[0]?.pin_code || 'Address not available'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4} sx={{ width: '30%' }} >
                <Card 
                  elevation={3} 
                  sx={{ 
                    borderRadius: 3,
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'primary.main',
                        borderRadius: '50%',
                        p: 1,
                        color: 'white'
                      }}>
                        <Payment sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Payment Information
                      </Typography>
                    </Stack>
                    <Stack spacing={2}>
                      
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip 
                          label={showorderlist[0]?.payment_status || 'Paid'} 
                          color="success" 
                          size="small" 
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4} sx={{ width: '30%' }} >
                <Card 
                  elevation={3} 
                  sx={{ 
                    borderRadius: 3,
                    height: '100%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Receipt sx={{ fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Order Summary
                      </Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Items Total:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ₹{showorderlist[0]?.grand_total - showorderlist[0]?.delivery_charge || '0.00'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Shipping:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ₹{showorderlist[0]?.delivery_charge || '0.00'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Tax:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ₹{showorderlist[0]?.tax || '0.00'}
                        </Typography>
                      </Stack>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          Total:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          ₹{showorderlist[0]?.grand_total}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>

      <Security />
      <Footer />
    </>
  );
};

export default TrackOrder;